// src/hooks/useMonetization.js
import { useState, useEffect, useCallback } from "react";
import {
  DEFAULT_SUBSCRIPTION,
  COIN_ECONOMICS,
  getDefaultDataSourceToggles,
} from "../components/config/monetization";

/**
 * useMonetization — owns all monetization state for the current user.
 *
 * State buckets:
 *   1. defaultSubscription   — auto-subscribe to new features by default
 *   2. dataSourceToggles     — opt-in/opt-out per dataset category
 *   3. coinBalance           — current Kleido Coin balance (display only;
 *                              actual ledger lives server-side)
 *
 * Everything is persisted to localStorage so a page refresh preserves the
 * user's choices. On first load, defaults come from monetization.js — never
 * from localStorage if absent, so a new user gets the safe defaults.
 */
export const useMonetization = () => {
  const [defaultSubscription, setDefaultSubscriptionState] = useState(
    DEFAULT_SUBSCRIPTION.DEFAULT,
  );
  const [dataSourceToggles, setDataSourceTogglesState] = useState(
    getDefaultDataSourceToggles(),
  );
  const [coinBalance, setCoinBalance] = useState(0);

  // ---- Load from localStorage on mount ----
  useEffect(() => {
    try {
      const savedDefault = localStorage.getItem(DEFAULT_SUBSCRIPTION.STORAGE_KEY);
      if (savedDefault !== null) {
        setDefaultSubscriptionState(savedDefault === "true");
      }

      const savedToggles = localStorage.getItem("monetization.dataSourceToggles");
      if (savedToggles) {
        const parsed = JSON.parse(savedToggles);
        // Merge with defaults so newly-added data sources get their defaults
        setDataSourceTogglesState({ ...getDefaultDataSourceToggles(), ...parsed });
      }

      const savedCoins = localStorage.getItem(COIN_ECONOMICS.STORAGE_KEY);
      if (savedCoins !== null) {
        setCoinBalance(Number(savedCoins) || 0);
      }
    } catch (err) {
      console.error("Failed to load monetization state:", err);
    }
  }, []);

  // ---- Setters that also persist ----
  const setDefaultSubscription = useCallback((value) => {
    setDefaultSubscriptionState(value);
    try {
      localStorage.setItem(DEFAULT_SUBSCRIPTION.STORAGE_KEY, String(value));
    } catch (err) {}
  }, []);

  const toggleDataSource = useCallback((sourceId) => {
    setDataSourceTogglesState((prev) => {
      const next = { ...prev, [sourceId]: !prev[sourceId] };
      try {
        localStorage.setItem(
          "monetization.dataSourceToggles",
          JSON.stringify(next),
        );
      } catch (err) {}
      return next;
    });
  }, []);

  const setDataSourceToggles = useCallback((next) => {
    setDataSourceTogglesState(next);
    try {
      localStorage.setItem(
        "monetization.dataSourceToggles",
        JSON.stringify(next),
      );
    } catch (err) {}
  }, []);

  return {
    // Default subscription
    defaultSubscription,
    setDefaultSubscription,

    // Data source toggles
    dataSourceToggles,
    toggleDataSource,
    setDataSourceToggles,

    // Coins (display only — real balance comes from server)
    coinBalance,
    setCoinBalance,
  };
};
